import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { AlertModal, ConfirmModal } from './AlertModal';
import MapImage, { githubImage, prettyMapName } from './maps/MapImage';
import MapPanel from './maps/MapPanel';
import './maps/maps.css';

// Pool rules (the server enforces the same maximums on add).
const POOLS = [
  { key: 'active_duty', label: 'Active Duty', max: 7, valid: (n) => n === 7 },
  { key: 'wingman', label: 'Wingman', max: 7, valid: (n) => [3, 5, 7].includes(n) },
  { key: 'competitive', label: 'Competitive', max: Infinity, valid: null }, // no veto, no limit
];

// Preset maps for each pool — one-click add (ghost cards)
const PRESET_MAPS = {
  active_duty: ['de_dust2', 'de_mirage', 'de_anubis', 'de_cache', 'de_ancient', 'de_nuke', 'de_inferno'],
  wingman: ['de_debris', 'de_eldorado', 'de_poseidon', 'de_overpass', 'de_vertigo', 'de_nuke', 'de_inferno'],
  competitive: ['de_ancient', 'de_vertigo', 'de_dust2', 'de_mirage', 'de_overpass', 'de_cache', 'de_inferno', 'de_anubis', 'de_nuke', 'de_train'],
};

const Icon = ({ d, size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">{d}</svg>
);
const I = {
  plus: <><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></>,
  refresh: <><polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" /></>,
  edit: <><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></>,
  trash: <><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></>,
  wrench: <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />,
};

function Maps() {
  const { t } = useTranslation();
  const [allMaps, setAllMaps] = useState([]);
  const [loading, setLoading] = useState(true);
  // Pool selection lives in the URL (?pool=...) so it survives refresh and is shareable.
  const [searchParams, setSearchParams] = useSearchParams();
  const poolParam = searchParams.get('pool');
  const pool = POOLS.find(p => p.key === poolParam) || POOLS[0];
  const setPool = (key) => setSearchParams(prev => { const n = new URLSearchParams(prev); n.set('pool', key); return n; }, { replace: true });

  const [panel, setPanel] = useState(null); // null | { mode: 'add' } | { mode: 'edit', map }
  const [saving, setSaving] = useState(false);
  const [quickAdding, setQuickAdding] = useState({}); // { mapName: true } while adding
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [alertModal, setAlertModal] = useState({ show: false, message: '', type: 'info' });
  const [confirmModal, setConfirmModal] = useState({ show: false, message: '', onConfirm: null });
  const toast = (message, type = 'success') => setAlertModal({ show: true, message, type });

  // One request for every pool (owner-scoped on the server) → tab counts without extra calls.
  const fetchMaps = useCallback(async () => {
    try {
      const res = await axios.get('/api/maps');
      setAllMaps(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setAlertModal({ show: true, message: t('mapsPage.loadFailed', 'Failed to load maps'), type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [t]);
  useEffect(() => { fetchMaps(); }, [fetchMaps]);

  const inPool = (key) => allMaps.filter(m => m.pool === key);
  const maps = inPool(pool.key);
  const room = Math.max(0, pool.max - maps.length);
  const existing = new Set(maps.map(m => m.name.toLowerCase()));
  const missing = (PRESET_MAPS[pool.key] || []).filter(n => !existing.has(n));
  const ghosts = room > 0 ? missing : [];

  const statusOf = (p, n) => (!p.valid ? 'none' : p.valid(n) ? 'ok' : 'warn');

  const addMap = async ({ name, image_url, workshop_id }) => {
    setSaving(true);
    try {
      await axios.post('/api/maps', { name, image_url, pool: pool.key, workshop_id });
      setPanel(null);
      toast(t('mapsPage.added', '{{name}} added', { name }));
      fetchMaps();
      return null;
    } catch (err) {
      return err.response?.data?.error || t('mapsPage.addFailed', 'Failed to add map');
    } finally { setSaving(false); }
  };

  const renameMap = async (map, name) => {
    setSaving(true);
    try {
      await axios.put(`/api/maps/${map.id}`, { name });
      setPanel(null);
      toast(t('mapsPage.renamed', 'Renamed to {{name}}', { name }));
      fetchMaps();
      return null;
    } catch (err) {
      return err.response?.data?.error || t('mapsPage.renameFailed', 'Failed to rename map');
    } finally { setSaving(false); }
  };

  const deleteMap = (map) => {
    setConfirmModal({
      show: true,
      message: t('mapsPage.deleteConfirm', 'Delete {{name}}? It will no longer be available for new matches.', { name: map.name }),
      onConfirm: async () => {
        setConfirmModal({ show: false, message: '', onConfirm: null });
        try {
          await axios.delete(`/api/maps/${map.id}`);
          toast(t('mapsPage.deleted', '{{name}} deleted', { name: map.name }));
          fetchMaps();
        } catch (err) {
          toast(err.response?.data?.error || t('mapsPage.deleteFailed', 'Failed to delete map'), 'error');
        }
      },
    });
  };

  const quickAdd = async (names) => {
    const list = names.slice(0, room);
    if (!list.length) return;
    setQuickAdding(prev => ({ ...prev, ...Object.fromEntries(list.map(n => [n, true])) }));
    let added = 0; let lastErr = '';
    for (const name of list) {
      try { await axios.post('/api/maps', { name, image_url: '', pool: pool.key }); added++; }
      catch (err) { lastErr = err.response?.data?.error || ''; }
    }
    setQuickAdding({});
    if (added) toast(added === 1 ? t('mapsPage.added', '{{name}} added', { name: list[0] }) : t('mapsPage.addedMany', '{{count}} maps added', { count: added }));
    else if (lastErr) toast(lastErr, 'error');
    fetchMaps();
  };

  const refreshImages = async () => {
    setIsRefreshing(true);
    try {
      const res = await axios.post('/api/maps/refresh-images');
      toast(res.data.message || t('mapsPage.imagesRefreshed', 'Map images refreshed'));
      fetchMaps();
    } catch (err) {
      toast(err.response?.data?.error || t('mapsPage.refreshFailed', 'Failed to refresh map images'), 'error');
    } finally { setIsRefreshing(false); }
  };

  const ruleText = () => {
    const n = maps.length;
    if (pool.key === 'active_duty') {
      return n === 7 ? t('mapsPage.ruleAdOk', 'Veto needs exactly 7 maps · ready')
        : t('mapsPage.ruleAdNeed', 'Veto needs exactly 7 maps · add {{count}} more', { count: 7 - n });
    }
    if (pool.key === 'wingman') {
      return [3, 5, 7].includes(n) ? t('mapsPage.ruleWmOk', 'Veto needs 3, 5 or 7 maps · ready')
        : t('mapsPage.ruleWmNeed', 'Veto needs 3, 5 or 7 maps · {{count}} now is not valid', { count: n });
    }
    return t('mapsPage.ruleComp', 'No veto and no limit - maps are picked by hand when creating a match.');
  };
  const st = statusOf(pool, maps.length);

  return (
    <div className="mp-page">
      <div className="mp-head">
        <div>
          <h1 className="mp-title">{t('maps.title', 'Map Pool')}</h1>
          <p className="mp-subtitle">{t('mapsPage.subtitle', 'Your maps for veto and match creation. Each admin has their own pool.')}</p>
        </div>
        <div className="mp-head-actions">
          <button type="button" className="mp-btn" onClick={refreshImages} disabled={isRefreshing} title={t('mapsPage.refreshTitle', 'Re-fetch map images from GitHub for all your maps')}>
            <span className={isRefreshing ? 'mp-spin-ic' : ''}><Icon d={I.refresh} /></span>
            {isRefreshing ? t('maps.refreshing', 'Refreshing...') : t('maps.refreshImages', 'Refresh Images')}
          </button>
          <button type="button" className="mp-btn primary" onClick={() => setPanel({ mode: 'add' })} disabled={room === 0} title={room === 0 ? t('mapsPage.poolFull', 'This pool is full ({{count}} maps)', { count: maps.length }) : undefined}>
            <Icon d={I.plus} /> {t('maps.addMap', 'Add Map')}
          </button>
        </div>
      </div>

      <div className="mp-tabs" role="tablist">
        {POOLS.map(p => {
          const n = inPool(p.key).length;
          const s = statusOf(p, n);
          return (
            <button key={p.key} type="button" role="tab" aria-selected={p.key === pool.key} className={`mp-tab${p.key === pool.key ? ' on' : ''}`} onClick={() => setPool(p.key)}>
              {t(`mapsPage.pool_${p.key}`, p.label)}
              <span className={`mp-tab-count${s !== 'none' ? ` ${s}` : ''}`}>{p.key === 'active_duty' ? `${n}/7` : n}</span>
            </button>
          );
        })}
      </div>

      <div className="mp-rulebar">
        <span className={`mp-rule ${st}`}>
          {st === 'ok' && <span className="mp-rule-ic" aria-hidden="true">✓</span>}
          {st === 'warn' && <span className="mp-rule-ic" aria-hidden="true">!</span>}
          {ruleText()}
        </span>
        {ghosts.length > 1 && (
          <button type="button" className="mp-btn small success" onClick={() => quickAdd(ghosts)} disabled={Object.keys(quickAdding).length > 0}>
            <Icon d={I.plus} size={11} /> {t('mapsPage.addMissing', 'Add {{count}} preset maps', { count: Math.min(ghosts.length, room) })}
          </button>
        )}
      </div>

      {loading ? (
        <div className="mp-grid">{Array.from({ length: 6 }).map((_, i) => <div key={i} className="mp-card skel" />)}</div>
      ) : maps.length === 0 && ghosts.length === 0 ? (
        <div className="mp-empty">{t('maps.noMaps', 'No maps in the {{pool}} pool. Add one to get started.', { pool: t(`mapsPage.pool_${pool.key}`, pool.label) })}</div>
      ) : (
        <div className="mp-grid">
          {maps.map(m => (
            <div key={m.id} className="mp-card">
              <MapImage className="mp-card-img" src={m.image_url} workshop={!!m.workshop_id} alt={m.name} />
              <div className="mp-card-shade" />
              {m.workshop_id && (
                <a className="mp-ws" href={`https://steamcommunity.com/sharedfiles/filedetails/?id=${m.workshop_id}`} target="_blank" rel="noopener noreferrer" title={t('maps.openWorkshop', 'Open Steam Workshop page')}>
                  <Icon d={I.wrench} size={12} /> {t('maps.workshopBadge', 'Workshop')}
                </a>
              )}
              <div className="mp-card-actions">
                <button type="button" onClick={() => setPanel({ mode: 'edit', map: m })} title={t('mapsPage.rename', 'Rename')} aria-label={t('mapsPage.rename', 'Rename')}><Icon d={I.edit} size={14} /></button>
                <button type="button" className="danger" onClick={() => deleteMap(m)} title={t('common.delete', 'Delete')} aria-label={t('common.delete', 'Delete')}><Icon d={I.trash} size={14} /></button>
              </div>
              <div className="mp-card-label">
                <span className="mp-card-name">{prettyMapName(m.name)}</span>
                <span className="mp-card-file"><span>{m.name}</span>{m.workshop_id && <span>{m.workshop_id}</span>}</span>
              </div>
            </div>
          ))}
          {ghosts.map(name => (
            <button key={name} type="button" className={`mp-card ghost${quickAdding[name] ? ' busy' : ''}`} onClick={() => !quickAdding[name] && quickAdd([name])} disabled={!!quickAdding[name]} title={t('mapsPage.quickAddTitle', 'Add {{name}} to this pool', { name })}>
              <MapImage className="mp-card-img" src={githubImage(name)} alt="" />
              <div className="mp-card-shade" />
              <span className="mp-ghost-plus">{quickAdding[name] ? <span className="mp-spin" /> : <Icon d={I.plus} size={16} />}</span>
              <div className="mp-card-label">
                <span className="mp-card-name">{prettyMapName(name)}</span>
                <span className="mp-card-file">{t('mapsPage.clickToAdd', 'Click to add')}</span>
              </div>
            </button>
          ))}
        </div>
      )}

      <MapPanel
        open={!!panel}
        editing={panel?.mode === 'edit' ? panel.map : null}
        poolLabel={t(`mapsPage.pool_${pool.key}`, pool.label)}
        busy={saving}
        onClose={() => setPanel(null)}
        onAdd={addMap}
        onRename={renameMap}
      />
      <AlertModal show={alertModal.show} message={alertModal.message} type={alertModal.type} onClose={() => setAlertModal({ ...alertModal, show: false })} />
      <ConfirmModal show={confirmModal.show} message={confirmModal.message} confirmText={t('common.delete', 'Delete')} type="danger" onConfirm={confirmModal.onConfirm} onClose={() => setConfirmModal({ ...confirmModal, show: false })} />
    </div>
  );
}

export default Maps;
